import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Receipt, Clock, User, Loader2, Calendar, Hash, Tag, Smartphone, Building2, CreditCard, Coins } from "lucide-react";
import { collection, getDocs, getDoc, doc, Timestamp } from "firebase/firestore";
import { db } from "@/firebase/config";

interface TransactionHistoryProps {
  schoolId: string | null;
  onBack: () => void;
}

interface TransactionRecord {
  id: string;
  studentName: string;
  amount: number;
  provider: string;
  status: string;
  transactionDate: any;
  reference: string;
  accountReference: string;
  termLabel?: string;
  termKey?: string;
  createdBy?: string;
  paymentType: string;
  mpesaDetails?: any;
}

const DEFAULT_PROVIDERS = ["cash", "mpesa", "equity", "kcb", "card"];

const PROVIDER_CONFIG: Record<string, { label: string; color: string; bg: string; darkBg: string; icon: React.ReactNode }> = {
  cash: {
    label: "Cash",
    color: "text-emerald-700 dark:text-emerald-400",
    bg: "bg-emerald-100",
    darkBg: "dark:bg-emerald-900/30",
    icon: <Coins className="h-3 w-3" />,
  },
  mpesa: {
    label: "M-Pesa",
    color: "text-green-700 dark:text-green-400",
    bg: "bg-green-100",
    darkBg: "dark:bg-green-900/30",
    icon: <Smartphone className="h-3 w-3" />,
  },
  equity: {
    label: "Equity Bank",
    color: "text-red-700 dark:text-red-400",
    bg: "bg-red-100",
    darkBg: "dark:bg-red-900/30",
    icon: <Building2 className="h-3 w-3" />,
  },
  kcb: {
    label: "KCB Bank",
    color: "text-blue-700 dark:text-blue-400",
    bg: "bg-blue-100",
    darkBg: "dark:bg-blue-900/30",
    icon: <Building2 className="h-3 w-3" />,
  },
  card: {
    label: "Card",
    color: "text-purple-700 dark:text-purple-400",
    bg: "bg-purple-100",
    darkBg: "dark:bg-purple-900/30",
    icon: <CreditCard className="h-3 w-3" />,
  },
};

// Fallback config for unknown providers
const DEFAULT_PROVIDER_CFG = {
  label: "Other",
  color: "text-gray-700 dark:text-gray-400",
  bg: "bg-gray-100",
  darkBg: "dark:bg-gray-800",
  icon: <Receipt className="h-3 w-3" />,
};

/**
 * Extracts a JS Date from a Firestore doc's various date fields.
 * Handles Timestamps, ISO strings, and M-Pesa's 14-digit format.
 */
function extractDate(data: any): Date {
  const fields = [
    data?.mpesaDetails?.transactionDate,
    data?.transactionDate,
    data?.timestamp,
    data?.createdAt,
    data?.completedAt,
    data?.lastUpdate,
  ];

  for (const f of fields) {
    if (!f) continue;
    // Firestore Timestamp
    if (typeof f?.toDate === "function") {
      try { return f.toDate(); } catch { /* skip */ }
    }
    // String dates
    if (typeof f === "string") {
      // M-Pesa 14-digit: YYYYMMDDHHmmss
      if (/^\d{14}$/.test(f)) {
        const Y = Number(f.slice(0, 4));
        const M = Number(f.slice(4, 6)) - 1;
        const D = Number(f.slice(6, 8));
        const h = Number(f.slice(8, 10));
        const m = Number(f.slice(10, 12));
        const s = Number(f.slice(12, 14));
        return new Date(Y, M, D, h, m, s);
      }
      const d = new Date(f);
      if (!isNaN(d.getTime())) return d;
    }
    // Number (epoch ms)
    if (typeof f === "number") {
      return new Date(f);
    }
  }
  return new Date(0);
}

const TransactionHistory: React.FC<TransactionHistoryProps> = ({ schoolId, onBack }) => {
  const [transactions, setTransactions] = useState<TransactionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [providers, setProviders] = useState<string[]>(DEFAULT_PROVIDERS);

  // Load dynamic payment providers from school config
  useEffect(() => {
    const loadProviders = async () => {
      if (!schoolId) return;
      try {
        const listSnap = await getDocs(collection(db, `schools/${schoolId}/systemConfig/paymentMethods/list`));
        if (!listSnap.empty) {
          const enabled: string[] = [];
          listSnap.forEach(d => {
            if (d.data().enabled) enabled.push(d.id);
          });
          if (enabled.length > 0) {
            setProviders(Array.from(new Set(enabled)));
            return;
          }
        }
        // Fallback: check single config doc
        const configSnap = await getDoc(doc(db, `schools/${schoolId}/systemConfig/paymentMethods`));
        if (configSnap.exists()) {
          const methods = configSnap.data().methods || [];
          const ids: string[] = [];
          methods.forEach((m: any) => {
            if (typeof m === "string") ids.push(m);
            else if (m.enabled && m.id) ids.push(m.id);
          });
          if (ids.length > 0) setProviders(Array.from(new Set(ids)));
        }
      } catch {
        // Use defaults
      }
    };
    loadProviders();
  }, [schoolId]);

  // Fetch all transactions from all providers
  useEffect(() => {
    const fetchAllTransactions = async () => {
      if (!schoolId) return;
      setLoading(true);
      try {
        const allTx: TransactionRecord[] = [];

        // 1. Fetch all students to build a regNumber → name map
        const studentsSnap = await getDocs(collection(db, `schools/${schoolId}/students`));
        const regToName: Record<string, string> = {};
        studentsSnap.docs.forEach(d => {
          const s = d.data();
          const regNum = s.registrationNumber || "";
          const fullName = [s.firstName, s.lastName].filter(Boolean).join(" ") || s.fullName || "";
          if (regNum && fullName) {
            regToName[regNum.toUpperCase()] = fullName;
          }
        });

        // 2. Fetch transactions from all providers
        const promises = providers.map(async (provider) => {
          try {
            const colRef = collection(db, `schools/${schoolId}/transactions/${provider}/records`);
            const snap = await getDocs(colRef);
            snap.docs.forEach(d => {
              const data = d.data();
              const acctRef = data.accountReference || data.BillRefNumber || "";

              // Try to resolve student name
              let studentName = data.studentName || data.FirstName || data.name || "";
              if (!studentName || studentName === "Unknown") {
                // Extract reg number from accountReference (e.g. "Fee-STU-VND02A7F" → "STU-VND02A7F")
                const regFromRef = acctRef.startsWith("Fee-") ? acctRef.slice(4) : acctRef;
                if (regFromRef && regToName[regFromRef.toUpperCase()]) {
                  studentName = regToName[regFromRef.toUpperCase()];
                }
              }

              allTx.push({
                id: d.id,
                studentName: studentName || "Unknown",
                amount: Number(data.amount) || 0,
                provider: data.provider || provider,
                status: data.status || "unknown",
                transactionDate: data.transactionDate || data.timestamp || data.createdAt || null,
                reference: data.reference || data.mpesaReceiptNumber || data.TransID || d.id,
                accountReference: acctRef || "—",
                termLabel: data.termLabel || "",
                termKey: data.termKey || "",
                createdBy: data.createdBy || "",
                paymentType: provider,
                mpesaDetails: data.mpesaDetails || null,
              });
            });
          } catch (err) {
            console.warn(`No records or error for provider "${provider}":`, err);
          }
        });

        await Promise.all(promises);

        // Sort all by date descending (client-side)
        allTx.sort((a, b) => {
          const aDate = extractDate(a);
          const bDate = extractDate(b);
          return bDate.getTime() - aDate.getTime();
        });

        setTransactions(allTx);
      } catch (err) {
        console.error("Failed to fetch transactions", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllTransactions();
  }, [schoolId, providers]);

  const filtered = transactions.filter(tx => {
    if (activeFilter !== "all" && tx.paymentType !== activeFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      tx.studentName?.toLowerCase().includes(q) ||
      tx.reference?.toLowerCase().includes(q) ||
      tx.accountReference?.toLowerCase().includes(q) ||
      tx.termLabel?.toLowerCase().includes(q)
    );
  });

  const formatDate = (tx: TransactionRecord) => {
    const d = extractDate(tx);
    if (d.getTime() === 0) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };

  // Count per provider for filter badges
  const providerCounts = providers.reduce((acc, p) => {
    acc[p] = transactions.filter(tx => tx.paymentType === p).length;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="flex flex-col h-full bg-transparent text-foreground overflow-y-auto p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          className="gap-2 w-fit border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </Button>
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Receipt className="h-6 w-6 text-emerald-600 dark:text-emerald-500" />
            Transaction History
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            View all recorded fee payments across all channels.
          </p>
        </div>
      </div>

      <div className="max-w-3xl w-full mx-auto space-y-4 pb-20">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by student name, reference, or term..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-10"
          />
        </div>

        {/* Filter Chips */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveFilter("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              activeFilter === "all"
                ? "bg-foreground text-background border-foreground"
                : "bg-transparent text-muted-foreground border-gray-300 dark:border-gray-700 hover:border-foreground"
            }`}
          >
            All ({transactions.length})
          </button>
          {providers.map((p) => {
            const cfg = PROVIDER_CONFIG[p] || { ...DEFAULT_PROVIDER_CFG, label: p.charAt(0).toUpperCase() + p.slice(1) };
            const count = providerCounts[p] || 0;
            if (count === 0) return null;
            return (
              <button
                key={p}
                onClick={() => setActiveFilter(p)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                  activeFilter === p
                    ? `${cfg.bg} ${cfg.darkBg} ${cfg.color} border-current`
                    : "bg-transparent text-muted-foreground border-gray-300 dark:border-gray-700 hover:border-foreground"
                }`}
              >
                {cfg.icon}
                {cfg.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Transaction List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin mb-3" />
            <p className="text-sm">Loading transactions...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Receipt className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">
              {searchQuery || activeFilter !== "all" ? "No matching transactions found." : "No transactions recorded yet."}
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">{filtered.length} transaction{filtered.length !== 1 ? "s" : ""}</p>
            {filtered.map((tx) => {
              const cfg = PROVIDER_CONFIG[tx.paymentType] || { ...DEFAULT_PROVIDER_CFG, label: tx.paymentType };
              return (
                <Card key={`${tx.paymentType}-${tx.id}`} className="shadow-sm border-gray-200 dark:border-gray-800 hover:shadow-md transition-shadow duration-200">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 space-y-2 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <User className="h-4 w-4 text-blue-500 shrink-0" />
                          <span className="font-semibold text-foreground truncate">{tx.studentName}</span>
                          {/* Payment Type Tag */}
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.darkBg} ${cfg.color}`}>
                            {cfg.icon}
                            {cfg.label}
                          </span>
                          {/* Status Badge */}
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            tx.status === "completed"
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                              : tx.status === "failed"
                                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                          }`}>
                            {tx.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Hash className="h-3 w-3" />
                            {tx.reference}
                          </span>
                          <span className="flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            {tx.accountReference}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(tx)}
                          </span>
                          {tx.termLabel && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {tx.termLabel}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                          KES {tx.amount?.toLocaleString() || "0"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
